<template>
  <div
    class="relative w-full cursor-pointer pt-4"
    role="button"
    tabindex="0"
    @click="toggleChartVisible"
    @keydown.enter.prevent="toggleChartVisible"
    @keydown.space.prevent="toggleChartVisible"
  >
    <BaseInnerLoading v-if="isTransactionCountsLoading" />
    <div
      v-if="isTransactionCountsError"
      class="mx-2 rounded-md border border-error p-2 text-xs text-error"
    >
      Error fetching transaction counts
    </div>

    <div
      v-else-if="!isChartVisible"
      class="grid grid-cols-3 grid-rows-1 place-items-center gap-4 px-8 text-center text-xs font-medium"
    >
      <div class="space-y-0.5">
        <div class="text-lg text-primary">{{
          isTransactionCountsLoading
            ? "-"
            : formatToNumberWithUnitPrefix(
                transactionCounts.countOverLast1Minute
              )
        }}</div>
        <div>1 min</div>
        <div
          v-if="isTransactionCountsLoading"
          class="text-[10px] text-background-fg/60"
        >
          -
        </div>
        <ComparisonChip
          v-else
          variant="sidebar"
          :direction="oneMinuteChangeMeta.direction"
          :tone="oneMinuteChangeMeta.tone"
          :value="formatChange(oneMinuteChange)"
          sr-label="Change over same 1 minute window last week"
        />
      </div>
      <div class="space-y-0.5">
        <div class="text-lg text-primary">{{
          isTransactionCountsLoading
            ? "-"
            : formatToNumberWithUnitPrefix(
                transactionCounts.countOverLast5Minutes
              )
        }}</div>
        <div>5 mins</div>
        <div
          v-if="isTransactionCountsLoading"
          class="text-[10px] text-background-fg/60"
        >
          -
        </div>
        <ComparisonChip
          v-else
          variant="sidebar"
          :direction="fiveMinuteChangeMeta.direction"
          :tone="fiveMinuteChangeMeta.tone"
          :value="formatChange(fiveMinuteChange)"
          sr-label="Change over same 5 minute window last week"
        />
      </div>
      <div class="space-y-0.5">
        <div class="text-lg text-primary">
          {{
            isTransactionCountsLoading
              ? "-"
              : formatToNumberWithUnitPrefix(
                  transactionCounts.countOverLast15Minutes
                )
          }}
        </div>
        <div>15 mins</div>
        <div
          v-if="isTransactionCountsLoading"
          class="text-[10px] text-background-fg/60"
        >
          -
        </div>
        <ComparisonChip
          v-else
          variant="sidebar"
          :direction="fifteenMinuteChangeMeta.direction"
          :tone="fifteenMinuteChangeMeta.tone"
          :value="formatChange(fifteenMinuteChange)"
          sr-label="Change over same 15 minute window last week"
        />
      </div>
    </div>

    <div v-else class="px-3 py-1">
      <div
        class="mb-2 text-center text-[10px] font-medium uppercase text-background-fg/60"
      >
        Transaction count (last 30 mins)
      </div>
      <div class="h-28">
        <LineChart
          :options="recentTransactionChartOptions"
          :data="recentTransactionChartData"
        />
      </div>
      <div
        class="mb-2 flex items-center justify-center gap-3 text-[10px] font-medium text-background-fg/60"
      >
        <div class="inline-flex items-center gap-1">
          <span
            class="block h-0 w-3 rounded-full border-t-2"
            :style="{
              borderColor: currentDatasetColor,
            }"
          />
          Now
        </div>
        <div class="inline-flex items-center gap-1">
          <span
            class="block h-0 w-3 border-t-2 border-dashed"
            :style="{
              borderColor: lastWeekDatasetColor,
            }"
          />
          This time last week
        </div>
      </div>
    </div>

    <div
      v-if="!isTransactionCountsError"
      class="absolute right-4 top-2"
    >
      <BaseToolTip
        text="Click anywhere in this panel to toggle chart view. Count view shows latest 1, 5 and 15 minute totals and comparisons against the same windows this time last week. Chart view compares the current six 5 minute windows against the same six windows this time last week."
        placement="bottom-start"
      >
        <IconInfo class="size-4" aria-hidden="true" />
      </BaseToolTip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Chart as ChartJS, registerables } from "chart.js"
import type { ChartData, ChartOptions } from "chart.js"
import { Line as LineChart } from "vue-chartjs"
import type { RetrieveRecentTransactionCountsResponse } from "@/services/transactions/retrieveRecentTransactionCounts"

ChartJS.register(...registerables)

const CHANGE_TOLERANCE = 0.3

type ComparisonDirection = "up" | "down" | "flat"

type ComparisonTone = "positive" | "negative" | "neutral"

type ComparisonMeta = {
  direction: ComparisonDirection
  tone: ComparisonTone
}

const emptyFiveMinuteWindowSeries = [0, 0, 0, 0, 0, 0]

const emptyRecentTransactionCounts: RetrieveRecentTransactionCountsResponse =
  {
    countOverLast1Minute: 0,
    countOverLast5Minutes: 0,
    countOverLast15Minutes: 0,
    countOverLast1MinuteThisTimeLastWeek: 0,
    countOverLast5MinutesThisTimeLastWeek: 0,
    countOverLast15MinutesThisTimeLastWeek: 0,
    countsOverLast30MinutesBy5MinuteWindow: [
      ...emptyFiveMinuteWindowSeries,
    ],
    countsOverLast30MinutesBy5MinuteWindowThisTimeLastWeek:
      [...emptyFiveMinuteWindowSeries],
  }

const {
  data: recentTransactionCounts,
  isFetching: isTransactionCountsLoading,
  isError: isTransactionCountsError,
} = useRecentTransactionCounts()

const transactionCounts = computed(
  () =>
    recentTransactionCounts.value ??
    emptyRecentTransactionCounts
)

const isChartVisible = ref(false)

const toggleChartVisible = () => {
  if (isTransactionCountsError.value) {
    return
  }

  isChartVisible.value = !isChartVisible.value
}

const formatChange = (change: number) =>
  formatToNumberWithUnitPrefix(change)

const getChangeDirection = (
  change: number
): ComparisonDirection => {
  if (change === 0) {
    return "flat"
  }

  return change > 0 ? "up" : "down"
}

const hasMaterialChange = (
  change: number,
  previousCount: number
) => {
  if (change === 0) {
    return false
  }

  if (previousCount === 0) {
    return true
  }

  return (
    Math.abs(change) / Math.abs(previousCount) >=
    CHANGE_TOLERANCE
  )
}

const getComparisonMeta = (
  currentCount: number,
  previousCount: number
): ComparisonMeta => {
  const change = currentCount - previousCount
  const direction = getChangeDirection(change)

  if (direction === "flat") {
    return {
      direction,
      tone: "neutral",
    }
  }

  const hasBigEnoughChange = hasMaterialChange(
    change,
    previousCount
  )

  if (!hasBigEnoughChange) {
    return {
      direction,
      tone: "neutral",
    }
  }

  return {
    direction,
    tone: direction === "up" ? "positive" : "negative",
  }
}

const oneMinuteChange = computed(
  () =>
    transactionCounts.value.countOverLast1Minute -
    transactionCounts.value
      .countOverLast1MinuteThisTimeLastWeek
)

const oneMinuteChangeMeta = computed(() =>
  getComparisonMeta(
    transactionCounts.value.countOverLast1Minute,
    transactionCounts.value
      .countOverLast1MinuteThisTimeLastWeek
  )
)

const fiveMinuteChange = computed(
  () =>
    transactionCounts.value.countOverLast5Minutes -
    transactionCounts.value
      .countOverLast5MinutesThisTimeLastWeek
)

const fiveMinuteChangeMeta = computed(() =>
  getComparisonMeta(
    transactionCounts.value.countOverLast5Minutes,
    transactionCounts.value
      .countOverLast5MinutesThisTimeLastWeek
  )
)

const fifteenMinuteChange = computed(
  () =>
    transactionCounts.value.countOverLast15Minutes -
    transactionCounts.value
      .countOverLast15MinutesThisTimeLastWeek
)

const fifteenMinuteChangeMeta = computed(() =>
  getComparisonMeta(
    transactionCounts.value.countOverLast15Minutes,
    transactionCounts.value
      .countOverLast15MinutesThisTimeLastWeek
  )
)

const colorPrimary = useCssVar("--color-primary")
const colorText = useCssVar("--color-on-background")

const currentDatasetColor = computed(
  () => `rgb(${colorPrimary.value})`
)
const lastWeekDatasetColor = computed(
  () => `rgb(${colorText.value} / 0.45)`
)

const chartWindowTickLabels = [
  "30m",
  "25m",
  "20m",
  "15m",
  "10m",
  "5m",
]

const chartWindowTooltipLabels = [
  "30-25m ago",
  "25-20m ago",
  "20-15m ago",
  "15-10m ago",
  "10-5m ago",
  "Last 5m",
]

const getTooltipDatasetLabel = (label: string) =>
  label === "This time last week" ? "Last wk" : label

const normalizeFiveMinuteWindowSeries = (
  series: number[]
) => {
  if (series.length >= emptyFiveMinuteWindowSeries.length) {
    return series.slice(
      0,
      emptyFiveMinuteWindowSeries.length
    )
  }

  return [
    ...series,
    ...new Array(
      emptyFiveMinuteWindowSeries.length - series.length
    ).fill(0),
  ]
}

const fiveMinuteWindowSeries = computed<number[]>(() =>
  normalizeFiveMinuteWindowSeries(
    transactionCounts.value
      .countsOverLast30MinutesBy5MinuteWindow
  )
)

const fiveMinuteWindowSeriesThisTimeLastWeek = computed<
  number[]
>(() =>
  normalizeFiveMinuteWindowSeries(
    transactionCounts.value
      .countsOverLast30MinutesBy5MinuteWindowThisTimeLastWeek
  )
)

const recentTransactionChartData = computed<
  ChartData<"line", number[], string>
>(() => ({
  labels: chartWindowTickLabels,
  datasets: [
    {
      label: "Now",
      data: fiveMinuteWindowSeries.value,
      borderColor: currentDatasetColor.value,
      backgroundColor: currentDatasetColor.value,
      pointRadius: 3,
      pointHoverRadius: 4,
      tension: 0.35,
    },
    {
      label: "This time last week",
      data: fiveMinuteWindowSeriesThisTimeLastWeek.value,
      borderColor: lastWeekDatasetColor.value,
      backgroundColor: lastWeekDatasetColor.value,
      borderDash: [4, 3],
      pointRadius: 2,
      pointHoverRadius: 3,
      tension: 0.35,
    },
  ],
}))

const recentTransactionChartOptions = computed<
  ChartOptions<"line">
>(() => ({
  maintainAspectRatio: false,
  animation: false,
  responsive: true,
  interaction: {
    mode: "index",
    intersect: true,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
      padding: 6,
      caretPadding: 4,
      bodySpacing: 2,
      callbacks: {
        title: (tooltipItems) => {
          const item = tooltipItems[0]

          if (!item) {
            return ""
          }

          return (
            chartWindowTooltipLabels[item.dataIndex] ?? ""
          )
        },
        label: (tooltipItem) =>
          `${getTooltipDatasetLabel(
            tooltipItem.dataset.label ?? ""
          )}: ${formatToNumberWithUnitPrefix(tooltipItem.parsed.y)}`,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        autoSkip: false,
        color: `rgb(${colorText.value} / 0.7)`,
        font: {
          size: 10,
        },
        crossAlign: "far",
        maxRotation: 0,
        minRotation: 0,
        padding: 2,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: `rgb(${colorText.value} / 0.05)`,
        drawTicks: false,
      },
      ticks: {
        color: `rgb(${colorText.value} / 0.7)`,
        font: {
          size: 10,
        },
        callback: (value: unknown) =>
          formatToNumberWithUnitPrefix(Number(value)),
      },
    },
  },
}))
</script>
